import { motion } from 'motion/react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  GitHubIcon,
  GitLabIcon,
  CircleCIIcon,
  VercelIcon,
  RailwayIcon,
  NodejsIcon,
  PythonIcon,
  TerminalIcon,
  KubernetesIcon,
  BitbucketIcon,
  NetlifyIcon,
  AWSLambdaIcon,
} from './icons';

export interface Integration {
  id: string;
  name: string;
  description: string;
  category: 'cli' | 'sdk' | 'cicd' | 'hosting' | 'kubernetes' | 'serverless';
  docsUrl?: string;
}

// Map integration IDs to their icon components
const INTEGRATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'github-actions': GitHubIcon,
  'gitlab-ci': GitLabIcon,
  'circleci': CircleCIIcon,
  'bitbucket-pipelines': BitbucketIcon,
  'vercel': VercelIcon,
  'railway': RailwayIcon,
  'netlify': NetlifyIcon,
  'node-sdk': NodejsIcon,
  'python-sdk': PythonIcon,
  'cli': TerminalIcon,
  'kubernetes-eso': KubernetesIcon,
  'aws-lambda': AWSLambdaIcon,
};

interface IntegrationCardProps {
  integration: Integration;
  onClick: () => void;
  className?: string;
}

export function IntegrationCard({ integration, onClick, className }: IntegrationCardProps) {
  const IconComponent = INTEGRATION_ICONS[integration.id] || TerminalIcon;

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      <Card
        onClick={onClick}
        className={cn(
          'glass border-border/50 cursor-pointer overflow-hidden group transition-all duration-200',
          'hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5',
          className
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <motion.div
              whileHover={{ rotate: [0, -5, 5, 0] }}
              transition={{ duration: 0.4 }}
              className="flex-shrink-0 p-2.5 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors"
            >
              <IconComponent className="h-5 w-5" />
            </motion.div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                {integration.name}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                {integration.description}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// List item version for sidebar/list layouts
interface IntegrationListItemProps {
  integration: Integration;
  isSelected: boolean;
  onClick: () => void;
}

export function IntegrationListItem({ integration, isSelected, onClick }: IntegrationListItemProps) {
  const IconComponent = INTEGRATION_ICONS[integration.id] || TerminalIcon;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200',
        'hover:bg-muted/50',
        isSelected && 'bg-primary/10 hover:bg-primary/15'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 p-2 rounded-lg transition-colors',
          isSelected ? 'bg-primary/20' : 'bg-muted/50'
        )}
      >
        <IconComponent className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-sm font-medium truncate transition-colors',
            isSelected ? 'text-primary' : 'text-foreground'
          )}
        >
          {integration.name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {integration.description}
        </div>
      </div>
      {isSelected && (
        <motion.div
          layoutId="selectedIndicator"
          className="w-1 h-8 bg-primary rounded-full"
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}
    </button>
  );
}
